import { Dispatch, SetStateAction, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonGroup, TextButton } from '../../../components';
import { ACTIVE_TAB_PARAM, AppRoutes, PUBLISH_PARAM } from '../../../constants';
import { useAppSelector, useCheckRequiredProspectFields, useSimActions } from '../../../hooks';
import { useGetOrganizationSettingsQuery } from '../../../services';
import {
  Permissions,
  ProspectLastTouchpoint,
  ProspectType,
  SimulationsTab,
  UpdatedPracticeProspectData,
} from '../../../types';
import { conditionalObject } from '../../../utils';

function parseUndefinedValueToNull<T>(value?: T, disabled?: boolean): T | null {
  return disabled || value === undefined ? null : value;
}

interface EditEndActionsProps {
  isSuperAdmin: boolean;
  resetForm: () => void;
  runRegeneratePrompt: () => Promise<void>;
  setRegeneratePromptDialog: Dispatch<SetStateAction<{ open: boolean; isDraft: boolean }>>;
}

const EditEndActions = ({
  isSuperAdmin,
  resetForm,
  runRegeneratePrompt,
  setRegeneratePromptDialog,
}: EditEndActionsProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPublishing = searchParams.get(PUBLISH_PARAM) === 'true';

  const { personaId, fields, type } = useAppSelector((state) => state.prospect);

  const user = useAppSelector((state) => state.auth.user);
  const canManageProspect = (user?.permissions || []).includes(Permissions.MANAGE_PROSPECT);

  const { data: orgConfigs } = useGetOrganizationSettingsQuery({});
  const { repCanEditLibrary = false } = orgConfigs || {};

  const canPublishSim = canManageProspect || repCanEditLibrary;

  const { runEdit, isEditing } = useSimActions();
  // Checks if all required fields are filled.
  const areRequiredFieldsFilled = useCheckRequiredProspectFields();
  const isSaveDisabled = !areRequiredFieldsFilled();

  // Handles canceling the edit mode by resetting the edited values.
  const handleCancel = useCallback(() => {
    resetForm();
    navigate(AppRoutes.SIMULATIONS);
  }, [resetForm, navigate]);

  // Handles saving the prospect, regenerating the prompt and navigating to the correct page.
  // For custom prospects, we do not allow regenerating the prompt.
  const onSaveSuccess = useCallback(
    (newData: UpdatedPracticeProspectData) => {
      const newIsDraft = newData.prospect.isDraft;

      // We regenerate the prompt for non-custom prospects.
      // Super-admins are given the option to regenerate the prompt.
      // Non-super admins have the prompt regenerated automatically.
      if (type !== ProspectType.CUSTOM) {
        if (isSuperAdmin) {
          // We remove the publish=true query param if it exists.
          if (isPublishing) {
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.delete(PUBLISH_PARAM);
              return newParams;
            });
          }

          // The prompt dialog handles navigating the super-admins to the Prompt tab after regenerating,
          // or navigating them back to the Simulations page after denying the prompt regeneration,
          // so we return early to prevent navigating after saving.
          setRegeneratePromptDialog({ open: true, isDraft: newIsDraft });
          return;
        } else {
          // We will consolidate regenerating the prompt into a single API call in the future.
          runRegeneratePrompt();
        }
      }

      // For custom prospects, or non-super admins, we navigate back to the correct tab after saving.
      // If the user is editing a draft, we navigate to the drafts tab.
      // Otherwise, we navigate to the library tab.
      const navigateToTab = newIsDraft ? SimulationsTab.DRAFTS : SimulationsTab.LIBRARY;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(ACTIVE_TAB_PARAM, navigateToTab);
      navigate({
        pathname: AppRoutes.SIMULATIONS,
        search: newSearchParams.toString(),
      });
    },
    [
      isPublishing,
      isSuperAdmin,
      type,
      runRegeneratePrompt,
      navigate,
      setRegeneratePromptDialog,
      searchParams,
      setSearchParams,
    ]
  );

  const handleSaveChanges = useCallback(() => {
    if (!personaId) return;

    const customFields = {
      // Only save the new prompt if it was edited by the user.
      ...conditionalObject(fields.isPromptEdited, {
        personaPrompt: fields.personaPrompt,
      }),
    };

    const nonCustomFields = {
      priorities: fields.priorities.map((priority) => priority.value).filter((priority) => !!priority),
      isDynamicPriorities: fields.isDynamicPriorities,
      numOfDynamicPriorities: parseUndefinedValueToNull(fields.numOfDynamicPriorities),
      objections: fields.objections.map((objection) => objection.value).filter((objection) => !!objection),
      isDynamicObjections: fields.isDynamicObjections,
      numOfDynamicObjections: parseUndefinedValueToNull(fields.numOfDynamicObjections),
      questions: fields.questions
        .filter((question) => !!question.value)
        .map((question) => ({
          id: question.id,
          question: question.value,
          answer: question.answer,
          points: question.points,
        })),
      isAnsweredQuestions: fields.isAnsweredQuestions,
      isDynamicQuestions: fields.isDynamicQuestions,
      numOfDynamicQuestions: parseUndefinedValueToNull(fields.numOfDynamicQuestions),
      lastTouchpoint: fields.lastTouchpoint.value === ProspectLastTouchpoint.NONE ? null : fields.lastTouchpoint.value,
      successResult: fields.successResult.value,
      hiddenContexts: fields.hiddenContexts.map((context) => context.value).filter((context) => !!context),
      successDifficultyLevel: fields.successDifficultyLevel.value,
    };

    runEdit({
      payload: {
        id: personaId,

        // Personal
        firstName: fields.firstName.value,
        lastName: fields.lastName.value,
        age: parseUndefinedValueToNull(fields.age.value),
        pronouns: parseUndefinedValueToNull(fields.pronouns.value),
        talkativenessLevel: fields.talkativenessLevel.value,
        figurativeThinkingLevel: fields.figurativeThinkingLevel.value,
        empathyLevel: fields.empathyLevel.value,
        improvisationLevel: fields.improvisationLevel.value,
        personaVoiceId: fields.personaVoiceId.value,
        language: fields.language,
        firstMessage: fields.firstMessage.value,

        // Lead
        jobTitle: fields.jobTitle.value,
        tenure: parseUndefinedValueToNull(fields.tenure.value),
        roleType: fields.roleType.value,
        department: fields.department.value,
        numOfDirectReports: parseUndefinedValueToNull(fields.numOfDirectReports.value),

        // Account
        accountName: fields.accountName.value,
        industry: fields.industry.value,
        hqLocation: fields.hqLocation.value,
        remoteType: parseUndefinedValueToNull(fields.remoteType.value),
        isMultiSite: fields.isMultiSite.value,
        employeesFrom: parseUndefinedValueToNull(fields.employeesFrom.value),
        employeesTo: parseUndefinedValueToNull(fields.employeesTo, fields.employeesFrom.value === undefined),
        annualRevenueFrom: parseUndefinedValueToNull(fields.annualRevenueFrom.value),
        annualRevenueTo: parseUndefinedValueToNull(
          fields.annualRevenueTo,
          fields.annualRevenueFrom.value === undefined
        ),
        fundingRaisedType: parseUndefinedValueToNull(fields.fundingRaisedType.value),

        // Scenario
        ...(type === ProspectType.CUSTOM ? customFields : nonCustomFields),

        // Configuration
        isHidden: fields.isHidden,
        screenStyle: fields.screenStyle,

        ...conditionalObject(canPublishSim, {
          folderId: fields.folderId,
        }),

        // Only pass manager settings fields if the user has manage prospect permissions.
        ...conditionalObject(canManageProspect, {
          scorecardTemplateId: parseUndefinedValueToNull(fields.scorecardTemplateId),
          coachingTemplateId: parseUndefinedValueToNull(fields.coachingTemplateId),
          cooldownPeriod: parseUndefinedValueToNull(fields.cooldownPeriod),
          timeLimit: parseUndefinedValueToNull(fields.timeLimit),
          managerNotes: parseUndefinedValueToNull(fields.managerNotes),
          allowedCallers: fields.allowedCallers.map((caller) => caller.id),
          allowedTeams: fields.allowedTeams.map((team) => team.id),
          isSubscribed: fields.isSubscribed,
        }),

        // Only pass super admin settings fields if the user has super admin permissions.
        ...conditionalObject(isSuperAdmin, {
          // Super-admins are allowed to edit the prompt for non-custom prospects.
          ...conditionalObject(type !== ProspectType.CUSTOM, customFields),
          isRestricted: fields.isRestricted,
          breathingRoom: parseUndefinedValueToNull(fields.breathingRoom),
          numOfInitialShortResponses: parseUndefinedValueToNull(fields.numOfInitialShortResponses),
          objectionRate: parseUndefinedValueToNull(fields.objectionRate),
          questionRate: parseUndefinedValueToNull(fields.questionRate),
          numOfRequiredUncoveredPriorities: parseUndefinedValueToNull(fields.numOfRequiredUncoveredPriorities),
          isHangupFeatureEnabled: fields.isHangupFeatureEnabled,
          hangupPrompt: fields.isHangupFeatureEnabled ? fields.hangupPrompt : null,
          buyNewPhoneNumber: fields.buyNewPhoneNumber,
          ttsModelId: fields.ttsModelId,
          sttModel: fields.sttModel,
          sttModelId: fields.sttModelId,
          llmModel: fields.llmModel,
          llmModelId: fields.llmModelId,
          llmTemperature: fields.llmTemperature,
          llmMaxTokens: fields.llmMaxTokens,
          llmFrequencyPenalty: parseUndefinedValueToNull(fields.llmFrequencyPenalty),
          llmPresencePenalty: parseUndefinedValueToNull(fields.llmPresencePenalty),
          promptTemplateId: fields.promptTemplateId,
        }),
      },
      onSuccess: onSaveSuccess,
    });
  }, [canManageProspect, canPublishSim, fields, isSuperAdmin, personaId, type, runEdit, onSaveSuccess, isPublishing]);

  return (
    <ButtonGroup loading={isEditing}>
      <TextButton
        disabled={isSaveDisabled}
        text={isPublishing ? 'Publish' : 'Save changes'}
        onClick={handleSaveChanges}
        tooltip={isSaveDisabled ? 'Please fill out all required fields before saving.' : undefined}
      />
      <TextButton text="Cancel" onClick={handleCancel} />
    </ButtonGroup>
  );
};

export default EditEndActions;
